/**
 * Theme DOM Effects
 *
 * Applies theme changes to the DOM via CSS custom properties and classes.
 * Separated from the store for easier testing and maintenance.
 *
 * @module store/theme/themeEffects
 */

import { themes, lightModeColors, darkModeColors, generatePaletteFromHex } from './themeConstants';
import logger from '../../shared/services/logging/logger';

/**
 * Apply theme to DOM by setting CSS custom properties and attributes
 * @param {string} themeName - Theme name
 * @param {boolean} isDarkMode - Dark mode enabled
 */
export function applyThemeToDOM(themeName, isDarkMode) {
  // Custom theme: "custom:#RRGGBB"
  let themeData;
  if (themeName && themeName.startsWith('custom:')) {
    const hex = themeName.slice(7);
    themeData = generatePaletteFromHex(hex);
  } else {
    themeData = themes[themeName] || themes.default;
  }

  logger.debug('Applying theme to DOM', { themeName, isDarkMode });

  // Set data-theme attribute for CSS (custom themes use 'custom')
  document.documentElement.setAttribute('data-theme', themeName?.startsWith('custom:') ? 'custom' : themeName);

  // Set dark mode class
  document.body.classList.toggle('dark-mode', isDarkMode);

  // Set theme CSS custom properties
  document.documentElement.style.setProperty('--primary-color', themeData.primary);

  // Set RGB version of primary color for rgba() usage in CSS
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : null;
  };
  const primaryRgb = hexToRgb(themeData.primary);
  if (primaryRgb) {
    document.documentElement.style.setProperty('--primary-color-rgb', primaryRgb);
  }

  document.documentElement.style.setProperty('--secondary-color', themeData.secondary);
  document.documentElement.style.setProperty('--accent-color', themeData.accent);
  document.documentElement.style.setProperty('--accentHighlight-color', themeData.accentHighlight);

  // Set theme border colors
  if (themeData.borderColors) {
    document.documentElement.style.setProperty('--theme-border-color', themeData.borderColors.theme);
    document.documentElement.style.setProperty('--theme-border-hover', themeData.borderColors.themeHover);
  }

  // Set theme shadow CSS properties
  if (themeData.shadows) {
    document.documentElement.style.setProperty('--shadowLight', themeData.shadows.light);
    document.documentElement.style.setProperty('--shadowDark', themeData.shadows.dark);
    document.documentElement.style.setProperty('--shadowMid', themeData.shadows.mid);
  }

  // Set gradient CSS properties
  if (themeData.gradients) {
    document.documentElement.style.setProperty('--gradient1', themeData.gradients.primary);
    document.documentElement.style.setProperty('--gradient2', themeData.gradients.secondary);
  }


  // Set path indicator property
  if (themeData.indicators?.path) {
    document.documentElement.style.setProperty('--path-indicator', themeData.indicators.path);
  }

  // Set primary tint vars (used by dropzones, hover states, drag indicators)
  const tintHover = themeData.tints?.hover || `rgba(${hexToRgb(themeData.primary)},${isDarkMode ? '0.15' : '0.07'})`;
  const tintDrag = themeData.tints?.drag || `rgba(${hexToRgb(themeData.primary)},${isDarkMode ? '0.25' : '0.14'})`;
  document.documentElement.style.setProperty('--primary-tint-hover', tintHover);
  document.documentElement.style.setProperty('--primary-tint-drag', tintDrag);

  // Set light/dark mode colors from SHARED definitions
  const currentColors = isDarkMode ? darkModeColors : lightModeColors;

  document.documentElement.style.setProperty('--background-color', currentColors.background);
  document.documentElement.style.setProperty('--surface-color', currentColors.surface);
  document.documentElement.style.setProperty('--text-primary', currentColors.textPrimary);
  document.documentElement.style.setProperty('--text-secondary', currentColors.textSecondary);
  document.documentElement.style.setProperty('--text-muted', currentColors.textMuted);
  document.documentElement.style.setProperty('--border-color', currentColors.borderColor);
  document.documentElement.style.setProperty('--card-background', currentColors.cardBackground);
  document.documentElement.style.setProperty('--input-background', currentColors.inputBackground);
  document.documentElement.style.setProperty('--shadow-base', currentColors.shadowBase);
  document.documentElement.style.setProperty('--filter-border-color', currentColors.filterBorderColor);
  document.documentElement.style.setProperty('--filter-border-hover', currentColors.filterBorderHover);

  // Set neumorphic shadows based on dark mode
  const neumorphicShadow = isDarkMode
    ? `1px 1px 2px 0px rgba(80, 80, 80, 0.30) inset,
       -1px -1px 2px 0px rgba(20, 20, 20, 0.50) inset,
       -10px 10px 20px 0px rgba(0, 0, 0, 0.40),
       10px -10px 20px 0px rgba(0, 0, 0, 0.40),
       -10px -10px 20px 0px rgba(80, 80, 80, 0.30),
       10px 10px 25px 0px rgba(0, 0, 0, 0.60)`
    : `1px 1px 2px 0px rgba(255, 255, 255, 0.30) inset,
       -1px -1px 2px 0px rgba(207, 207, 207, 0.50) inset,
       -10px 10px 20px 0px rgba(207, 207, 207, 0.20),
       10px -10px 20px 0px rgba(207, 207, 207, 0.20),
       -10px -10px 20px 0px rgba(255, 255, 255, 0.90),
       10px 10px 25px 0px rgba(207, 207, 207, 0.90)`;

  // Set box shadow
  const boxShadow = isDarkMode
    ? `inset -1px -1px 2px rgba(40, 40, 40, 0.6),
       inset 1px 1px 2px rgba(90, 90, 90, 0.4)`
    : `inset -1px -1px 2px rgba(213, 213, 213, 0.5),
       inset 1px 1px 2px rgba(255, 255, 255, 0.3)`;

  // Set intense box shadow
  const intenseBoxShadow = isDarkMode
    ? `inset 1px 1px 3px rgba(40, 43, 50, 0.9),
       inset -1px -1px 3px rgba(80, 85, 95, 0.9),
       inset 1px -1px 2px rgba(40, 43, 50, 0.2),
       inset -1px 1px 2px rgba(40, 43, 50, 0.2)`
    : `inset 1px 1px 3px rgba(199, 199, 199, 0.9),
       inset -1px -1px 3px rgba(255, 255, 255, 0.9),
       inset 1px -1px 2px rgba(199, 199, 199, 0.2),
       inset -1px 1px 2px rgba(199, 199, 199, 0.2)`;

  document.documentElement.style.setProperty('--neumorphic-shadow', neumorphicShadow);
  document.documentElement.style.setProperty('--box-shadow', boxShadow);
  document.documentElement.style.setProperty('--intense-box-shadow', intenseBoxShadow);

  // Neumorphic modal/card design tokens (project modals, QIS design system)
  if (isDarkMode) {
    document.documentElement.style.setProperty('--neu-card-shadow',
      '-6px -6px 12px 0 rgba(60,60,60,0.5), 6px 6px 12px 0 rgba(0,0,0,0.5)');
    document.documentElement.style.setProperty('--neu-btn-shadow',
      '4px 4px 8px 0 rgba(0,0,0,0.5), -4px -4px 8px 0 rgba(60,60,60,0.3)');
    document.documentElement.style.setProperty('--neu-btn-shadow-hover',
      '2px 2px 5px 0 rgba(0,0,0,0.6), -2px -2px 5px 0 rgba(60,60,60,0.4)');
    document.documentElement.style.setProperty('--neu-btn-shadow-sm',
      '2px 2px 5px 0 rgba(0,0,0,0.5), -2px -2px 5px 0 rgba(60,60,60,0.3)');
    document.documentElement.style.setProperty('--neu-inset-shadow',
      '4px 4px 8px 0 rgba(0,0,0,0.4) inset, -4px -4px 8px 0 rgba(60,60,60,0.25) inset');
    document.documentElement.style.setProperty('--neu-inset-focus',
      '4px 4px 8px 0 rgba(0,0,0,0.5) inset, -4px -4px 8px 0 rgba(60,60,60,0.3) inset');
    document.documentElement.style.setProperty('--neu-modal-shadow',
      '10px 10px 20px 0 rgba(0,0,0,0.6)');
    document.documentElement.style.setProperty('--neu-border', 'rgba(80,80,80,0.6)');
    document.documentElement.style.setProperty('--neu-surface', 'rgba(40,40,40,0.8)');
    document.documentElement.style.setProperty('--neu-card-inset',
      '4px 4px 8px 0 rgba(0,0,0,0.4) inset, -4px -4px 8px 0 rgba(60,60,60,0.2) inset');
    document.documentElement.style.setProperty('--neu-label-color', 'rgba(180,180,180,0.9)');
    document.documentElement.style.setProperty('--neu-placeholder', 'rgba(120,120,120,0.8)');
    document.documentElement.style.setProperty('--neu-selected-icon-bg', 'rgba(255,255,255,0.1)');
  } else {
    document.documentElement.style.setProperty('--neu-card-shadow',
      '-6px -6px 12px 0 rgba(255,255,255,0.8), 6px 6px 12px 0 rgba(166,180,200,0.5)');
    document.documentElement.style.setProperty('--neu-btn-shadow',
      '4px 4px 8px 0 rgba(166,180,200,0.5), -4px -4px 8px 0 rgba(255,255,255,0.9)');
    document.documentElement.style.setProperty('--neu-btn-shadow-hover',
      '2px 2px 5px 0 rgba(166,180,200,0.6), -2px -2px 5px 0 rgba(255,255,255,1)');
    document.documentElement.style.setProperty('--neu-btn-shadow-sm',
      '2px 2px 5px 0 #D1D9E6, -2px -2px 5px 0 #FFF');
    document.documentElement.style.setProperty('--neu-inset-shadow',
      '4px 4px 8px 0 #D1D9E6 inset, -4px -4px 8px 0 #FFF inset');
    document.documentElement.style.setProperty('--neu-inset-focus',
      '4px 4px 8px 0 #C8CEDB inset, -4px -4px 8px 0 #FFF inset');
    document.documentElement.style.setProperty('--neu-modal-shadow',
      '10px 10px 20px 0 rgba(166,180,200,0.7)');
    document.documentElement.style.setProperty('--neu-border', '#FEFEFF');
    document.documentElement.style.setProperty('--neu-surface', '#E5E9EB');
    document.documentElement.style.setProperty('--neu-card-inset',
      '4px 4px 8px 0 #D0D5D8 inset, -4px -4px 8px 0 #FFF inset');
    document.documentElement.style.setProperty('--neu-label-color', '#595C5E');
    document.documentElement.style.setProperty('--neu-placeholder', '#ABABAB');
    document.documentElement.style.setProperty('--neu-selected-icon-bg', 'rgba(255,255,255,0.55)');
  }
}

/**
 * Dispatch custom theme changed event
 * @param {string} themeName - Theme name
 * @param {string} hex - Theme primary hex color
 */
export function dispatchThemeChangedEvent(themeName, hex) {
  window.dispatchEvent(
    new CustomEvent('themeChanged', {
      detail: { theme: themeName, hex }
    })
  );
}
